/**
 * apps/web :: lib/rulediff-browse-states
 *
 * Catalog of US destination jurisdictions with full reviewer-approved
 * rule-diff seed coverage in `content/rule-diff/seed/`. Used ONLY by
 * the rule-diff browse page (`pages/RuleDiffBrowsePage.tsx`) — distinct
 * from `lib/states.ts::US_STATES`/`UsStateCode` which is the narrower
 * 10-state catalog used by the onboarding-wizard + profile binding.
 *
 * Why a separate catalog? `UsStateCode` is a chokepoint enum that
 * cascades through the kmp-shared-core profile schema, the
 * analytics/StateCode union, the backend DB constraint, etc. — adding
 * a destination here without launching the full state experience is
 * not viable. The browse page is read-only display (not persisted to
 * profile, not exam-eligible) so it can safely surface all 56
 * jurisdictions present in the seed corpus.
 *
 * Lockstep with content/rule-diff/seed/{cc}-XX.json filenames. As the
 * corpus grows (Wave 17b added all 50 states + DC + 5 territories;
 * Wave 17c added Chile across all 56), append/update here.
 *
 * 56 entries as of Wave 17d (2026-04-26):
 *   - 50 US states
 *   - 1 federal district (DC)
 *   - 5 US territories (PR, VI, GU, AS, MP)
 *
 * The `isLhs` flag is `true` ONLY for VI (US Virgin Islands) — the
 * single US jurisdiction that drives on the LEFT (Danish colonial
 * legacy until 1917). Surfacing this lets the picker provide a
 * subtle visual hint, and the seed corpus's side_of_road entries
 * encode the safety-critical bilateral context per home country.
 */

export interface BrowseState {
  /** Lowercase 2-letter code matching the seed filename suffix (e.g. "ca" from mx-ca.json). */
  code: string;
  /** English display name. */
  name: string;
  /**
   * Geographic grouping for the picker `<optgroup>` — keeps the long
   * dropdown navigable. Categories: "state" (50 US states),
   * "district" (DC), "territory" (PR/VI/GU/AS/MP).
   */
  category: "state" | "district" | "territory";
  /**
   * `true` if the jurisdiction drives on the LEFT. Only USVI in the
   * 56-jurisdiction set; everything else is RHS. The seed corpus's
   * uk-vi / au-vi / pk-vi etc. entries encode the RARE-MATCH
   * bilateral framing for LHS countries.
   */
  isLhs: boolean;
}

export const BROWSE_STATES: readonly BrowseState[] = [
  // ── 50 US states (alphabetical) ──────────────────────────────
  { code: "ak", name: "Alaska", category: "state", isLhs: false },
  { code: "al", name: "Alabama", category: "state", isLhs: false },
  { code: "ar", name: "Arkansas", category: "state", isLhs: false },
  { code: "az", name: "Arizona", category: "state", isLhs: false },
  { code: "ca", name: "California", category: "state", isLhs: false },
  { code: "co", name: "Colorado", category: "state", isLhs: false },
  { code: "ct", name: "Connecticut", category: "state", isLhs: false },
  { code: "de", name: "Delaware", category: "state", isLhs: false },
  { code: "fl", name: "Florida", category: "state", isLhs: false },
  { code: "ga", name: "Georgia", category: "state", isLhs: false },
  { code: "hi", name: "Hawaii", category: "state", isLhs: false },
  { code: "ia", name: "Iowa", category: "state", isLhs: false },
  { code: "id", name: "Idaho", category: "state", isLhs: false },
  { code: "il", name: "Illinois", category: "state", isLhs: false },
  { code: "in", name: "Indiana", category: "state", isLhs: false },
  { code: "ks", name: "Kansas", category: "state", isLhs: false },
  { code: "ky", name: "Kentucky", category: "state", isLhs: false },
  { code: "la", name: "Louisiana", category: "state", isLhs: false },
  { code: "ma", name: "Massachusetts", category: "state", isLhs: false },
  { code: "md", name: "Maryland", category: "state", isLhs: false },
  { code: "me", name: "Maine", category: "state", isLhs: false },
  { code: "mi", name: "Michigan", category: "state", isLhs: false },
  { code: "mn", name: "Minnesota", category: "state", isLhs: false },
  { code: "mo", name: "Missouri", category: "state", isLhs: false },
  { code: "ms", name: "Mississippi", category: "state", isLhs: false },
  { code: "mt", name: "Montana", category: "state", isLhs: false },
  { code: "nc", name: "North Carolina", category: "state", isLhs: false },
  { code: "nd", name: "North Dakota", category: "state", isLhs: false },
  { code: "ne", name: "Nebraska", category: "state", isLhs: false },
  { code: "nh", name: "New Hampshire", category: "state", isLhs: false },
  { code: "nj", name: "New Jersey", category: "state", isLhs: false },
  { code: "nm", name: "New Mexico", category: "state", isLhs: false },
  { code: "nv", name: "Nevada", category: "state", isLhs: false },
  { code: "ny", name: "New York", category: "state", isLhs: false },
  { code: "oh", name: "Ohio", category: "state", isLhs: false },
  { code: "ok", name: "Oklahoma", category: "state", isLhs: false },
  { code: "or", name: "Oregon", category: "state", isLhs: false },
  { code: "pa", name: "Pennsylvania", category: "state", isLhs: false },
  { code: "ri", name: "Rhode Island", category: "state", isLhs: false },
  { code: "sc", name: "South Carolina", category: "state", isLhs: false },
  { code: "sd", name: "South Dakota", category: "state", isLhs: false },
  { code: "tn", name: "Tennessee", category: "state", isLhs: false },
  { code: "tx", name: "Texas", category: "state", isLhs: false },
  { code: "ut", name: "Utah", category: "state", isLhs: false },
  { code: "va", name: "Virginia", category: "state", isLhs: false },
  { code: "vt", name: "Vermont", category: "state", isLhs: false },
  { code: "wa", name: "Washington", category: "state", isLhs: false },
  { code: "wi", name: "Wisconsin", category: "state", isLhs: false },
  { code: "wv", name: "West Virginia", category: "state", isLhs: false },
  { code: "wy", name: "Wyoming", category: "state", isLhs: false },
  // ── Federal district ─────────────────────────────────────────
  { code: "dc", name: "District of Columbia", category: "district", isLhs: false },
  // ── US territories ───────────────────────────────────────────
  { code: "as", name: "American Samoa", category: "territory", isLhs: false },
  { code: "gu", name: "Guam", category: "territory", isLhs: false },
  { code: "mp", name: "Northern Mariana Islands", category: "territory", isLhs: false },
  { code: "pr", name: "Puerto Rico", category: "territory", isLhs: false },
  { code: "vi", name: "US Virgin Islands", category: "territory", isLhs: true },
] as const;

/** Lookup helper. Returns `undefined` if the code isn't in the catalog. */
export function getBrowseState(code: string): BrowseState | undefined {
  const lc = code.toLowerCase();
  return BROWSE_STATES.find((s) => s.code === lc);
}

/**
 * Group the catalog by category for `<optgroup>` rendering. Returns
 * arrays in the same order as BROWSE_STATES.
 */
export function browseStatesByCategory(): {
  states: BrowseState[];
  district: BrowseState[];
  territories: BrowseState[];
} {
  return {
    states: BROWSE_STATES.filter((s) => s.category === "state"),
    district: BROWSE_STATES.filter((s) => s.category === "district"),
    territories: BROWSE_STATES.filter((s) => s.category === "territory"),
  };
}
